import React, { useState, useRef } from 'react';

export default function ReplyBox({ isNoteMode, onToggleNote, onSend, sending, allowMedia = false }) {
  const [text, setText] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const media = allowMedia && !isNoteMode && mediaUrl.trim() ? { mediaUrl: mediaUrl.trim(), mediaType } : null;
  const textareaRef = useRef(null);

  const handleSend = async () => {
    if ((!text.trim() && !media) || sending) return;
    await onSend(text.trim(), media);
    setText('');
    setMediaUrl('');
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`border-t ${isNoteMode ? 'border-[#FFB75D] bg-[#FFF4E5]' : 'border-[#E5E5E5] bg-white'} px-4 py-3`}>
      {draft && draft.text && !isNoteMode && (
        <div className="mb-2 rounded-lg border border-[#0176D3]/30 bg-[#EAF5FE] px-3 py-2 text-xs text-[#032D60]">
          <p className="font-semibold mb-1">AI suggested reply{draft.priority && draft.priority !== 'normal' ? ` · ${draft.priority} priority` : ''}</p>
          <p className="whitespace-pre-wrap mb-1">{draft.text}</p>
          {draft.reason && <p className="text-[#5C5C5C] mb-1">Why: {draft.reason}</p>}
          <div className="flex gap-3">
            <button type="button" className="font-semibold text-[#0176D3] hover:underline" onClick={() => { setText(draft.text); onDraftUsed && onDraftUsed(); textareaRef.current?.focus(); }}>Use this draft</button>
            <button type="button" className="text-[#5C5C5C] hover:underline" onClick={() => onDraftDismissed && onDraftDismissed()}>Dismiss</button>
          </div>
        </div>
      )}
      {isNoteMode && (
        <p className="text-xs text-[#DD7A01] font-medium mb-2">Internal Note — only visible to your team</p>
      )}
      {allowMedia && !isNoteMode && (
        <div className="flex gap-2 mb-2">
          <select value={mediaType} onChange={e => setMediaType(e.target.value)} className="border border-[#E5E5E5] rounded px-2 py-1 text-xs bg-white">
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="document">Document</option>
          </select>
          <input value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="Optional public https link to send as media (your text becomes the caption)"
            className="flex-1 border border-[#E5E5E5] rounded px-3 py-1 text-xs bg-[#F7F9FB] focus:outline-none focus:ring-2 focus:ring-[#0176D3]" />
        </div>
      )}
      <div className="flex gap-2 items-end">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isNoteMode ? 'Add an internal note…' : 'Type a reply... (Enter to send, Shift+Enter for new line)'}
          rows={2}
          className={`flex-1 resize-none rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
            isNoteMode
              ? 'border border-[#FFB75D] bg-[#FFF4E5] focus:ring-[#FE9339] placeholder-yellow-400'
              : 'border border-[#E5E5E5] bg-[#F7F9FB] focus:ring-[#0176D3] placeholder-[#706E6B]'
          }`}
        />
        <div className="flex flex-col gap-1.5">
          <button
            onClick={onToggleNote}
            title={isNoteMode ? 'Switch to reply' : 'Add internal note'}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              isNoteMode
                ? 'bg-[#FFF4E5] text-[#8C4B02] hover:bg-[#FFB75D]'
                : 'bg-[#F3F2F2] text-[#5C5C5C] hover:bg-[#E5E5E5]'
            }`}
          >
            {isNoteMode ? '💬 Reply' : '📝 Note'}
          </button>
          <button
            onClick={handleSend}
            disabled={(!text.trim() && !media) || sending}
            className="px-4 py-1.5 bg-[#0176D3] hover:bg-[#014486] disabled:bg-[#B8DAF3] text-white rounded text-xs font-semibold transition-colors whitespace-nowrap"
          >
            {sending ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
